import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import waterVertexShader from './shaders/water.vert?raw'
import waterFragmentShader from './shaders/water.frag?raw'
import causticsVertexShader from './shaders/caustics.vert?raw'
import causticsFragmentShader from './shaders/caustics.frag?raw'
import { Pane } from 'tweakpane'

function setupUI({ waterResolution, water, ground }) {
  const pane = new Pane()

  const waterFolder = pane.addFolder({title:'Water'})

  const geometryFolder = waterFolder.addFolder({ title:'Geometry'})
  geometryFolder.addBinding(waterResolution,'size',{min:2,max:1024,step:2,label:'Resolution'}).on('change',({value})=>{
    console.log(value)
    // Update geometry with new dimensions
    const newGeometry = new THREE.PlaneGeometry(2,2,waterResolution.size,waterResolution.size)
    water.geometry.dispose()
    water.geometry = newGeometry
  })

  const wavesFolder = waterFolder.addFolder({title:'Waves'})
  wavesFolder.addBinding(water.material.uniforms.uWavesAmplitude,  'value',{min:0, max:.1,label:'Amplitude'})
  wavesFolder.addBinding(water.material.uniforms.uWavesFrequency,  'value',{min:.1,max:10,label:'Frequency'})
  wavesFolder.addBinding(water.material.uniforms.uWavesPersistence,'value',{min:0, max: 1,label:'Persistence'})
  wavesFolder.addBinding(water.material.uniforms.uWavesLacunarity, 'value',{min:0, max: 3,label:'Lacunarity'})
  wavesFolder.addBinding(water.material.uniforms.uWavesIterations, 'value',{min:1, max:10,label:'Iterations',step:1})
  wavesFolder.addBinding(water.material.uniforms.uWavesSpeed,      'value',{min:0, max: 1,label:'Speed'})
  
  const colorFolder = waterFolder.addFolder({title:'Color'})
  colorFolder.addBinding(water.material.uniforms.uTroughColor,'value',{label:'Trough Color',view:'color',color:{type:'float'}})
  colorFolder.addBinding(water.material.uniforms.uSurfaceColor,'value',{label:'Surface Color',view:'color',color:{type:'float'}})
  colorFolder.addBinding(water.material.uniforms.uPeakColor,   'value',{label:'Peak Color',view:'color',color:{type:'float'}})
  colorFolder.addBinding(water.material.uniforms.uOpacity,         'value',{min:0,max:1,step:.01,label:'Opacity'})
  colorFolder.addBinding(water.material.uniforms.uPeakThreshold,   'value',{min:0,max:.5,label:'Peak Threshold'})
  colorFolder.addBinding(water.material.uniforms.uPeakTransition,  'value',{min:0,max:.5,label:'Peak Transition'})
  colorFolder.addBinding(water.material.uniforms.uTroughThreshold, 'value',{min:-.5,max:0,label:'Trough Threshold'})
  colorFolder.addBinding(water.material.uniforms.uTroughTransition,'value',{min:0,max:.5,label:'Trough Transition'})
  
  const fresnelFolder = waterFolder.addFolder({ title: 'Fresnel' })
  fresnelFolder.addBinding(water.material.uniforms.uFresnelScale,'value',{min:0,max:1,label:'Scale'})
  fresnelFolder.addBinding(water.material.uniforms.uFresnelPower,'value',{min:0,max:3,label:'Power'})
  
  const causticsFolder = waterFolder.addFolder({ title: 'Caustics' })
  causticsFolder.addBinding(ground.material.uniforms.uCausticsColor,    'value',{label:'Color',view:'color',color:{type:'float'}})
  causticsFolder.addBinding(ground.material.uniforms.uCausticsIntensity,'value',{min:0,max:2,label:'Intensity'})
  causticsFolder.addBinding(ground.material.uniforms.uCausticsScale,    'value',{min:0,max:200,label:'Scale'})
  causticsFolder.addBinding(ground.material.uniforms.uCausticsSpeed,    'value',{min:0,max:1,label:'Speed'})
  causticsFolder.addBinding(ground.material.uniforms.uCausticsOffset,   'value',{min:0, max:2,label:'Offset'})
  causticsFolder.addBinding(ground.material.uniforms.uCausticsThickness,'value',{min:0,max:1,label:'Thickness'})
}
class Water extends THREE.Mesh {
  constructor(options = {}) {
    super()

    this.material = new THREE.ShaderMaterial({
      vertexShader: waterVertexShader,
      fragmentShader: waterFragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uOpacity: { value: .8 },
        uEnvironmentMap: { value: options.environmentMap },
        uWavesAmplitude: { value: .025 },
        uWavesFrequency: { value: 1.07 },
        uWavesPersistence: { value: .3 },
        uWavesLacunarity: { value: 2.18 },
        uWavesIterations: { value: 8 },
        uWavesSpeed: { value: .4 },
        uTroughColor: { value: new THREE.Color('#186691') },
        uSurfaceColor: { value: new THREE.Color('#9bd8c0') },
        uPeakColor: { value: new THREE.Color('#bbd8e0') },
        uPeakThreshold: { value: .08 },
        uPeakTransition: { value: .05 },
        uTroughThreshold: { value: -.01 },
        uTroughTransition: { value: .15 },
        uFresnelScale: { value: .8 },
        uFresnelPower: { value: .5 }
      },
      transparent: true,
      depthTest: true,
      side: THREE.DoubleSide
    })
    this.geometry = new THREE.PlaneGeometry(2, 2, options.resolution || 512, options.resolution || 512)
    this.rotation.x = Math.PI * .5
    this.position.y = 0
  }
  update(time) {
    this.material.uniforms.uTime.value = time
  }
} 
class Ground extends THREE.Mesh {
  constructor(options = {}) {
    super()
    this.material = new THREE.ShaderMaterial({
      vertexShader: causticsVertexShader,
      fragmentShader: causticsFragmentShader,
      uniforms: {
        uTexture: { value: options.texture },
        uTime: { value: 0 },
        uCausticsColor: { value: new THREE.Color('#ffffff') },
        uCausticsIntensity: { value: .2 },
        uCausticsScale: { value: 20.0 },
        uCausticsSpeed: { value: 1.0 },
        uCausticsThickness: { value: .4 },
        uCausticsOffset: { value: .75 }
      }
    })
    this.geometry = new THREE.PlaneGeometry(2, 2)
    this.rotation.x = -Math.PI * .5
    this.position.y = -.12
  }
  update(time) {
    this.material.uniforms.uTime.value = time
  }
} 
const clock = new THREE.Clock()
const scene = new THREE.Scene()
const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, .001, 100)
const renderer = new THREE.WebGLRenderer({ antialias: true })
renderer.setSize(window.innerWidth, window.innerHeight)
renderer.setPixelRatio(devicePixelRatio)
document.body.appendChild(renderer.domElement)

const cubeTextureLoader = new THREE.CubeTextureLoader()
const environmentMap=cubeTextureLoader.load(['posX.png','negX.png','posY.png','negY.png','posZ.png','negZ.png'])
const oceanFloorTexture = new THREE.TextureLoader().load('ocean_floor.png')
scene.background = environmentMap
scene.environment = environmentMap
camera.position.set(.8, .03, 0)
const orbitcontrols = new OrbitControls(camera, renderer.domElement)
orbitcontrols.enableDamping = true
const ambientLight = new THREE.AmbientLight(0xffffff, .8)
scene.add(ambientLight)
const waterResolution = { size: 512 }
const water = new Water({environmentMap,resolution: waterResolution.size})
scene.add(water)
const ground = new Ground({texture: oceanFloorTexture})
scene.add(ground)
//////////////////////////////////////////
const poolWidth =2.032
const poolLength=2.032
const poolHeight=.2

const poolTileTexture = new THREE.TextureLoader().load('pooltiles-diffuse.jpg')

const wallMaterial = new THREE.MeshStandardMaterial({map:poolTileTexture,side:THREE.BackSide})
const leftWall = new THREE.Mesh(new THREE.BoxGeometry(.05, poolHeight, poolLength), wallMaterial)
leftWall.position.set(-poolWidth / 2,-.03, 0)
scene.add(leftWall)
const rightWall = new THREE.Mesh(new THREE.BoxGeometry(.05, poolHeight, poolLength), wallMaterial)
rightWall.position.set(poolWidth / 2,-.03, 0)
scene.add(rightWall)
const frontWall = new THREE.Mesh(new THREE.BoxGeometry(poolWidth, poolHeight, .05), wallMaterial)
frontWall.position.set(0,-.03, poolLength / 2)
scene.add(frontWall)
const backWall = new THREE.Mesh(new THREE.BoxGeometry(poolWidth, poolHeight, .05), wallMaterial)
backWall.position.set(0,-.03, -poolLength / 2)
scene.add(backWall)
//////////////////////////////////////////
function animate() {
  const elapsedTime = clock.getElapsedTime()
  water.update(elapsedTime)
  ground.update(elapsedTime)
  orbitcontrols.update()
  renderer.render(scene, camera)
  requestAnimationFrame(animate)
}
window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
})
animate()
setupUI({ waterResolution, water, ground })